import { useState } from 'react'
import { Trash2, CalendarDays, ExternalLink, ChevronDown, ChevronRight, MessageSquare, UserCheck } from 'lucide-react'
import { TEMPLATE_BADGE_COLORS, getTemplateBadgeLabel, isOverdue } from '../utils/taskHelpers'
import type { PresalesTask, PresalesTaskStatus } from '@/api/types'

// ==================== Kanban Column ====================

export function KanbanColumn({
  title,
  count,
  colorClass,
  icon,
  status,
  tasks,
  onDrop,
  onDelete,
  onEdit,
  collapsible,
}: {
  title: string
  count: number
  colorClass: string
  icon: React.ReactNode
  status: PresalesTaskStatus
  tasks: PresalesTask[]
  onDrop: (taskId: number, newStatus: PresalesTaskStatus) => void
  onDelete: (task: PresalesTask) => void
  onEdit: (task: PresalesTask) => void
  collapsible?: boolean
}) {
  const [collapsed, setCollapsed] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const showTasks = collapsible ? !collapsed : true

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOver(true)
  }

  const handleDragLeave = () => {
    setDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const taskId = parseInt(e.dataTransfer.getData('text/plain'))
    if (!isNaN(taskId)) {
      onDrop(taskId, status)
    }
  }

  return (
    <div className="flex flex-col">
      <button
        onClick={() => collapsible && setCollapsed(!collapsed)}
        className={`flex items-center gap-2 px-3 py-2 rounded-t-lg border-b-2 ${colorClass} ${collapsible ? 'cursor-pointer' : 'cursor-default'}`}
      >
        {icon}
        <span className="text-xs font-semibold uppercase tracking-wide">{title}</span>
        <span className="text-xs opacity-70">({count})</span>
        {collapsible && (
          collapsed ? <ChevronRight className="h-3.5 w-3.5 ml-auto" /> : <ChevronDown className="h-3.5 w-3.5 ml-auto" />
        )}
      </button>
      <div
        className={`flex-1 bg-[var(--bg-secondary)] rounded-b-lg border border-t-0 min-h-[120px] transition-colors ${
          dragOver ? 'border-primary bg-primary/5' : 'border-[var(--border-color)]'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {showTasks && tasks.length === 0 && (
          <div className="flex items-center justify-center h-full py-8 text-xs text-[var(--text-muted)]">
            {dragOver ? 'Drop here' : 'No tasks'}
          </div>
        )}
        {showTasks && (
          <div className="p-2 space-y-1">
            {tasks.map((task) => (
              <KanbanTaskCard
                key={task.id}
                task={task}
                onDelete={onDelete}
                onEdit={onEdit}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ==================== Kanban Task Card ====================

function formatDateShort(dateStr: string) {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function KanbanTaskCard({
  task,
  onDelete,
  onEdit,
}: {
  task: PresalesTask
  onDelete: (task: PresalesTask) => void
  onEdit: (task: PresalesTask) => void
}) {
  const isCompleted = task.status === 'completed'
  const overdue = isOverdue(task)
  const truncatedDeal = task.deal_name
    ? task.deal_name.length > 12 ? task.deal_name.slice(0, 12) + '…' : task.deal_name
    : null
  const descriptionLong = task.description.length > 50

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(task.id))
        e.dataTransfer.effectAllowed = 'move'
      }}
      onClick={() => onEdit(task)}
      className={`bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-2.5 group transition-colors hover:border-[var(--text-muted)]/30 cursor-pointer ${isCompleted ? 'opacity-60' : ''}`}
    >
      {/* Row 1: Deal name (left) | Due date (right) */}
      <div className="flex items-center justify-between gap-2 text-[11px] text-[var(--text-muted)] mb-1">
        <div className="flex items-center gap-1 min-w-0">
          {truncatedDeal && (
            <span className="flex items-center gap-1 truncate" title={task.deal_name || ''}>
              <ExternalLink className="h-3 w-3 flex-shrink-0" />
              {truncatedDeal}
            </span>
          )}
          {task.template !== 'other' && (
            <span className={`inline-block px-1 py-0 text-[9px] font-medium rounded border ${TEMPLATE_BADGE_COLORS[task.template] || TEMPLATE_BADGE_COLORS.other}`}>
              {getTemplateBadgeLabel(task.template)}
            </span>
          )}
        </div>
        {task.due_date && (
          <span className={`flex items-center gap-1 flex-shrink-0 ${overdue ? 'text-red-400 font-medium' : ''}`}>
            <CalendarDays className="h-3 w-3" />
            {formatDateShort(task.due_date)}
            {overdue && ' !'}
          </span>
        )}
      </div>

      {/* Row 2: Description + delete icon */}
      <div className="flex items-start justify-between gap-1.5">
        <div className="flex-1 min-w-0">
          <span className={`text-sm leading-snug ${isCompleted ? 'line-through text-[var(--text-muted)]' : 'text-[var(--text-primary)]'}`}>
            {descriptionLong ? task.description.slice(0, 50) + '…' : task.description}
          </span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(task) }}
          className="p-1 rounded text-[var(--text-muted)] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0 mt-0.5"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Hard deadline / critical path flags */}
      {!!(task.is_hard_deadline || task.is_critical_path) && (
        <div className="mt-1 flex items-center gap-1.5">
          {!!task.is_hard_deadline && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0 text-[9px] font-medium rounded border bg-red-900/30 text-red-300 border-red-700/40">
              🚨 Hard Deadline
            </span>
          )}
          {!!task.is_critical_path && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0 text-[9px] font-medium rounded border bg-orange-900/30 text-orange-300 border-orange-700/40">
              🔴 Critical Path
            </span>
          )}
        </div>
      )}

      {/* Key person + status update preview */}
      {(task.key_person || task.status_update) && (
        <div className="mt-1 space-y-0.5">
          {task.key_person && (
            <p className="text-[11px] text-[var(--text-muted)] flex items-center gap-1 truncate">
              <UserCheck className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{task.key_person}</span>
            </p>
          )}
          {task.status_update && (
            <p className="text-[11px] text-amber-300/80 flex items-center gap-1 truncate">
              <MessageSquare className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{task.status_update}</span>
            </p>
          )}
        </div>
      )}
    </div>
  )
}
